import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { PUBLISHED_TXT, TOAST_CLASSES } from "../utils/constants";
import {
  deleteSolutionAPI,
  getSolutionsAPI,
  createSolutionAPI,
  editSolutionAPI,
  getSolutionAPI,
  getSolutionAllVersionsAPI,
  approveSolutionAPI,
} from "../apis/solution.api";
import { getLookupValuesAPI } from "../apis/common.api.js";
import { AuthContext } from "../AuthContext";
import { Backdrop, CircularProgress } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDashboard } from "./DashboardContext";
import { fetchAllAgents } from "../apis/agent.details.api";

const SolutionContext = createContext();

const getInitialSolutionData = () => ({
  solution_name: "",
  description: "",
  agents: [],
  point_of_contact: "",
  url: "",
  solution_metadata: {},
  approval_status: "",
  rejection_reason: "",
  delete_solution: 0,
  ecosystem: "GCP",
  active: true,
  type: "",
  classifications: [
    {
      industry_info: [
        {
          industry: "",
          sector: [],
        },
      ],
      business_function: [],
    },
  ],
  metadata: [
    {
      documentation_url: "",
      provider: "",
    },
    {
      streaming: true,
      push_notifications: true,
      state_transition_history: true,
    },
  ],
});

export const SolutionsProvider = ({ children }) => {
  const { showToastMessage } = useDashboard();
  const location = useLocation();

  const [isFetchingSolutions, setIsFetchingSolutions] = useState(false);
  const [isRegisteringOrEditingSol, setIsRegisteringOrEditingSol] =
    useState(false);
  const [isSilentLoading, setIsSilentLoading] = useState(false);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isApprovingRejecting, setIsApprovingRejecting] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isLoadingDropdownFields, setIsLoadingDropdownFields] = useState({
    providers: false,
    industries: false,
    sectors: false,
    agents: false,
    bFs: false,
    status: false,
  });
  const [isFetchingSectorsSilently, setIsFetchingSectorsSilently] =
    useState(false);

  const [isInitializingEdit, setIsInitializingEdit] = useState(false);

  const [lookUpValues, setLookUpValues] = useState({
    providers: [],
    industries: [],
    sectors: [],
    agents: [],
    bFs: [],
    status: [],
  });
  const [solutions, setSolutions] = useState([]);
  const [selectedSolution, setSelectedSolution] = useState(null);

  const [agentsForSol, setAgentsForSol] = useState([]);

  const initialSolutionData = getInitialSolutionData();
  const [ongoingSolutionData, setOngoingSolutionData] =
    useState(initialSolutionData);

  const [solutionCardOpen, setSolutionCardOpen] = useState(false);

  const [showLoader, setShowLoader] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [solutionVersions, setSolutionVersions] = useState([]);
  const [currentSolutionVersion, setCurrentSolutionVersion] = useState(null);

  const [versionsFetchError, setVersionsFetchError] = useState(false);
  const [agentsFetchError, setAgentsFetchError] = useState(false);

  const [isLatestVersion, setIsLatestVersion] = useState(false);

  const [solutionDetailsDrawerOpen, setSolutionDetailsDrawerOpen] =
    useState(false);

  // Required fields validation state
  const [requiredFields, setRequiredFields] = useState({
    solution_name: false,
    point_of_contact: false,
    description: false,
    agents: false,
    documentation_url: false,
  });

  const { organisationId, projectId, authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;

  const navigate = useNavigate();

  // Show backdrop spinner
  const showSpinner = useCallback((isOpen) => {
    setShowLoader(isOpen);
  }, []);

  const changeSolutionDrawerState = useCallback((bool) => {
    setSolutionDetailsDrawerOpen(bool);
  }, []);

  const validateIndustrySector = useCallback((industryInfo) => {
    const updatedIndustryInfo = [];

    for (const item of industryInfo) {
      let updatedIndustry = item?.industry;
      let updatedSector = item?.sector;

      if (updatedSector && typeof updatedIndustry !== "string") {
        updatedIndustry = "";
      } else if (typeof updatedIndustry === "string" && !updatedSector) {
        updatedSector = [];
      }

      updatedIndustryInfo.push({
        industry: updatedIndustry,
        sector: updatedSector,
      });
    }

    return updatedIndustryInfo;
  }, []);

  // ------------------------------ API Functions START ------------------------------

  const fetchSolutions = useCallback(async () => {
    setIsFetchingSolutions(true);

    try {
      const response = await getSolutionsAPI({
        organisationId,
        projectId,
        access_token,
        token_type,
      });

      if (!response || !Array.isArray(response?.data)) {
        setSolutions([]);

        showToastMessage("No solutions found", TOAST_CLASSES?.ERROR);
      } else {
        setSolutions(response?.data || []);
      }
    } catch (err) {
      showToastMessage("Failed to fetch solutions", TOAST_CLASSES?.ERROR);

      setSolutions([]);

      console.error("Error fetching solutions:", err);
    } finally {
      setIsFetchingSolutions(false);
    }
  }, [organisationId, projectId, access_token, token_type, showToastMessage]);

  const fetchSolutionsSilently = useCallback(async () => {
    setIsSilentLoading(true);

    try {
      const response = await getSolutionsAPI({
        organisationId,
        projectId,
        access_token,
        token_type,
      });

      if (response && Array.isArray(response?.data)) {
        setSolutions(response?.data || solutions);
      } else {
        showToastMessage("Failed to fetch latest data.", TOAST_CLASSES?.ERROR);
      }
    } catch (err) {
      showToastMessage("Failed to fetch latest data.", TOAST_CLASSES?.ERROR);
      console.error("Error fetching solutions in background:", err);
    } finally {
      setIsSilentLoading(false);
    }
  }, [
    organisationId,
    projectId,
    access_token,
    token_type,
    showToastMessage,
    solutions,
  ]);

  const deleteSolution = useCallback(
    async (solutionId) => {
      try {
        setIsDeleting(true);

        const response = await deleteSolutionAPI(
          { organisationId, projectId, access_token, token_type },
          solutionId
        );

        if (response?.status === 200) {
          showToastMessage(
            "Solution has been deleted successfully.",
            TOAST_CLASSES?.SUCCESS
          );

          await fetchSolutionsSilently();
        } else {
          showToastMessage("Failed to delete solution", TOAST_CLASSES?.ERROR);
        }
      } catch (error) {
        showToastMessage("Failed to delete solution", TOAST_CLASSES?.ERROR);
      } finally {
        setIsDeleting(false);
      }
    },
    [
      organisationId,
      projectId,
      access_token,
      token_type,
      showToastMessage,
      fetchSolutionsSilently,
    ]
  );

  const fetchSolutionVersions = useCallback(
    async (solutionId) => {
      try {
        setIsLoadingVersions(true);
        setVersionsFetchError(false);

        const headData = {
          organisationId,
          projectId,
          access_token,
          token_type,
        };

        const response = await getSolutionAllVersionsAPI(headData, solutionId);

        if (response && response?.data) {
          setSolutionVersions(response?.data || []);
          setVersionsFetchError(false);
        } else {
          setVersionsFetchError(true);
          showToastMessage(
            "Failed to fetch versions, showing current version.",
            TOAST_CLASSES?.ERROR
          );
        }
      } catch (err) {
        console.error("falied to fetch version: ", err);
        setVersionsFetchError(true);
        showToastMessage(
          "Failed to fetch versions, showing current version.",
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsLoadingVersions(false);
      }
    },
    [organisationId, projectId, access_token, token_type, showToastMessage]
  );

  // Check if all required fields are valid
  const areAllRequiredFieldsValid = useCallback(() => {
    return Object.values(requiredFields).every((isValid) => isValid);
  }, [requiredFields]);

  const registerSolution = useCallback(async () => {
    if (areAllRequiredFieldsValid()) {
      const payload = ongoingSolutionData;

      const industryInfo = payload?.classifications?.[0]?.industry_info || [];

      const updatedIndustryInfo = validateIndustrySector(industryInfo);

      if (payload?.classifications?.[0]) {
        payload.classifications[0].industry_info = updatedIndustryInfo;
      }

      try {
        setIsRegisteringOrEditingSol(true);

        const response = await createSolutionAPI(
          {
            organisationId,
            projectId,
            access_token,
            token_type,
          },
          payload
        );

        if (!response) {
          showToastMessage("Failed to register solution", TOAST_CLASSES?.ERROR);
        } else {
          showToastMessage(
            "Solution has been registered successfully.",
            TOAST_CLASSES?.SUCCESS
          );
        }
      } catch (err) {
        showToastMessage("Failed to register solution.", TOAST_CLASSES?.ERROR);
      } finally {
        setIsRegisteringOrEditingSol(false);
        navigate("/solutions", { replace: true });
      }
    }
  }, [
    organisationId,
    projectId,
    access_token,
    token_type,
    ongoingSolutionData,
    showToastMessage,
    navigate,
    areAllRequiredFieldsValid,
    validateIndustrySector,
  ]);

  const editSolution = useCallback(
    async (solutionId) => {
      if (areAllRequiredFieldsValid()) {
        const payload = ongoingSolutionData;

        const industryInfo = payload?.classifications?.[0]?.industry_info || [];

        const updatedIndustryInfo = validateIndustrySector(industryInfo);

        if (payload?.classifications?.[0]) {
          payload.classifications[0].industry_info = updatedIndustryInfo;
        }

        try {
          setIsRegisteringOrEditingSol(true);

          const response = await editSolutionAPI(
            {
              organisationId,
              projectId,
              access_token,
              token_type,
            },
            solutionId,
            { ...payload, solution_id: "" }
          );

          if (response) {
            showToastMessage("Solution Edits has been saved successfully.");
          } else {
            showToastMessage("Failed to edit solution.", TOAST_CLASSES?.ERROR);
          }
        } catch (err) {
          showToastMessage("Failed to edit solution.", TOAST_CLASSES?.ERROR);
        } finally {
          setIsRegisteringOrEditingSol(false);
          navigate("/solutions", { replace: true });
        }
      }
    },
    [
      organisationId,
      projectId,
      access_token,
      token_type,
      ongoingSolutionData,
      showToastMessage,
      navigate,
      areAllRequiredFieldsValid,
      validateIndustrySector,
    ]
  );

  const getSolution = useCallback(
    async (solutionId) => {
      try {
        setIsFetchingSolutions(true);

        const response = await getSolutionAPI(
          {
            organisationId,
            projectId,
            access_token,
            token_type,
          },
          solutionId
        );

        return response?.data;
      } catch (err) {
        console.error("Error fetching solution details:", err);
        showToastMessage(
          "Failed to fetch solution details",
          TOAST_CLASSES?.ERROR
        );
        return null;
      } finally {
        setIsFetchingSolutions(false);
      }
    },
    [organisationId, projectId, access_token, token_type, showToastMessage]
  );

  const publishSolution = useCallback(
    async (solutionId, approvalStatus, rejectionReason) => {
      try {
        setIsApprovingRejecting(true);
        const payloadData = {
          approval_status: approvalStatus,
          rejection_reason: rejectionReason || "",
        };

        const response = await approveSolutionAPI(
          {
            organisationId,
            projectId,
            access_token,
            token_type,
          },
          solutionId,
          payloadData
        );

        if (response) {
          showToastMessage(
            `Solution has been ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
              ? "published"
              : "unpublished"
            } successfully.`,
            TOAST_CLASSES?.SUCCESS
          );
        } else {
          showToastMessage(
            `Failed to ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
              ? "publish"
              : "unpublish"
            } the solution.`,
            TOAST_CLASSES?.ERROR
          );
        }
      } catch (err) {
        showToastMessage(
          `Failed to ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
            ? "publish"
            : "unpublish"
          } the solution.`,
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsApprovingRejecting(false);
        changeSolutionDrawerState(false);
      }
    },
    [
      organisationId,
      projectId,
      access_token,
      token_type,
      showToastMessage,
      changeSolutionDrawerState,
    ]
  );

  const fetchAgents = useCallback(async () => {
    setIsLoadingAgents(true);

    try {
      const response = await fetchAllAgents({
        queryKey: [
          "agents",
          { organisationId, projectId, access_token, token_type },
        ],
      });

      if (!response) {
        setAgentsForSol([]);
        showToastMessage("No agents found!", TOAST_CLASSES?.ERROR);
        return null;
      } else {
        setAgentsForSol(response?.data || []);
        return response;
      }
    } catch (err) {
      console.error("Error fetching agents:", err);
      setAgentsForSol([]);
      showToastMessage("Failed to fetch agents", TOAST_CLASSES?.ERROR);
      setAgentsFetchError(true);
      return null;
    } finally {
      setIsLoadingAgents(false);
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  //  Lookup API Calls Start --------------------------------------------------------------------

  const fetchSolutionBFs = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      bFs: true,
    }));

    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "solutions_category");

      if (response?.data && Array.isArray(response?.data)) {
        const categories = [
          ...response?.data.map((category) => {
            if (typeof category === "string") {
              return category;
            }

            return category.lookup_value || String(category);
          }),
        ];

        setLookUpValues((prev) => ({
          ...prev,
          bFs: categories || [],
        }));

        return categories;
      } else {
        setLookUpValues((prev) => ({
          ...prev,
          bFs: [],
        }));

        return [];
      }
    } catch (error) {
      showToastMessage("Failed to fetch categories", TOAST_CLASSES?.ERROR);

      setLookUpValues((prev) => ({
        ...prev,
        bFs: [],
      }));

      return [];
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        bFs: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchSolutionProviders = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      providers: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "solution_provider");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch providers", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((provider) => provider?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        providers: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching solution providers");
      showToastMessage(
        "Failed to fetch solution providers",
        TOAST_CLASSES?.ERROR
      );
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        providers: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchSolutionIndustries = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      industries: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "industry");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch industries", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((industry) => industry?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        industries: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching solution industries");
      showToastMessage(
        "Failed to fetch solution industries",
        TOAST_CLASSES?.ERROR
      );
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        industries: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchSolutionSectors = useCallback(
    async (industry) => {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        sectors: true,
      }));
      try {
        const headData = {
          organisationId,
          projectId,
          access_token,
          token_type,
        };

        const response = await getLookupValuesAPI(
          headData,
          `industry:${industry}`
        );

        if (!response || !response?.data) {
          showToastMessage("Failed to fetch sectors", TOAST_CLASSES?.ERROR);
          return;
        }

        const data = response?.data?.map((sector) => sector?.lookup_value);

        setLookUpValues((prev) => ({
          ...prev,
          sectors: data || [],
        }));

        return data || [];
      } catch {
        console.error("Error fetching solution sectors");
        showToastMessage(
          "Failed to fetch solution sectors",
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsLoadingDropdownFields((prev) => ({
          ...prev,
          sectors: false,
        }));
      }
    },
    [access_token, organisationId, projectId, token_type, showToastMessage]
  );

  const fetchSolutionSectorsSilently = useCallback(
    async (industry) => {
      setIsFetchingSectorsSilently(true);
      try {
        const headData = {
          organisationId,
          projectId,
          access_token,
          token_type,
        };

        const response = await getLookupValuesAPI(
          headData,
          `industry:${industry}`
        );

        if (!response || !response?.data) {
          showToastMessage("Failed to fetch sectors", TOAST_CLASSES?.ERROR);
          return;
        }

        const data = response?.data?.map((sector) => sector?.lookup_value);

        // setLookUpValues((prev) => ({
        //   ...prev,
        //   sectors: data || [],
        // }));

        return data || [];
      } catch {
        console.error("Error fetching solution sectors");
        showToastMessage(
          "Failed to fetch solution sectors",
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsFetchingSectorsSilently(false);
      }
    },
    [access_token, organisationId, projectId, token_type, showToastMessage]
  );

  const fetchSolutionStatus = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      status: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "solution_status");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch status", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((status) => status?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        status: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching solution status");
      showToastMessage("Failed to fetch solution status", TOAST_CLASSES?.ERROR);
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        status: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  // ------------------------------------------------------------------------ Lookup API Calls End

  // ------------------------------- API Functions END ------------------------------

  // Validation function for required fields
  const validateRequiredField = useCallback((field, value) => {
    let isValid = false;

    switch (field) {
      case "solution_name":
      case "point_of_contact":
        isValid = !!value && value.trim() !== "";
        break;
      case "description":
        // Check for empty rich text content
        isValid =
          !!value &&
          value.trim() !== "" &&
          value !== "<p></p>" &&
          value !== "<p><br></p>";
        break;
      case "agents":
        isValid = Array.isArray(value) && value.length > 0;
        break;
      case "documentation_url":
        isValid = !!value && value.trim() !== "";
        break;
      default:
        isValid = !!value;
    }

    setRequiredFields((prev) => ({
      ...prev,
      [field]: isValid,
    }));
  }, []);

  // Reset validation state
  const resetValidation = useCallback(() => {
    setRequiredFields({
      solution_name: false,
      point_of_contact: false,
      description: false,
      agents: false,
      documentation_url: false,
    });
  }, []);

  // Function to set solutions (for loading from API)
  const setSolutionsData = useCallback((solutionsData) => { }, []);

  const selectSolution = useCallback((solution) => {
    setSelectedSolution(solution);
  }, []);

  const selectSolVersion = useCallback((version) => {
    setCurrentSolutionVersion(version);
  }, []);

  const clearSelectedSol = useCallback(() => {
    setSelectedSolution(null);
  }, []);

  const clearSolVersions = useCallback(() => {
    setSolutionVersions(null);
  }, []);

  const clearCurrentSolVersion = useCallback(() => {
    setCurrentSolutionVersion(null);
  }, []);

  // Function to update ongoing solution data
  const updateOngoingSolDataL1 = useCallback((field, data) => {
    setOngoingSolutionData((prevData) => ({
      ...prevData,
      [field]: data,
    }));
  }, []);

  const updateOngoingSolDataL2 = useCallback((field, data) => {
    setOngoingSolutionData((prevData) => ({
      ...prevData,
      metadata: [
        { ...prevData?.metadata?.[0], [field]: data },
        prevData?.metadata?.[1],
      ],
    }));
  }, []);

  // Update metadata[1] (capabilities)
  const updateOngoingSolDataL2Capabilities = useCallback((field, data) => {
    setOngoingSolutionData((prevData) => ({
      ...prevData,
      metadata: [
        prevData?.metadata?.[0],
        { ...prevData?.metadata?.[1], [field]: data },
      ],
    }));
  }, []);

  const updateOngoingSolDataL3 = useCallback((field, data) => {
    if (field === "business_function") {
      setOngoingSolutionData((prevData) => ({
        ...prevData,
        classifications: [
          {
            ...prevData?.classifications?.[0],
            business_function: data,
          },
        ],
      }));
    } else if (field === "industry_info") {
      setOngoingSolutionData((prevData) => ({
        ...prevData,
        classifications: [
          {
            ...prevData?.classifications?.[0],
            industry_info: data,
          },
        ],
      }));
    }
  }, []);

  // Function to clear ongoing solution data
  const clearOngoingSolutionData = useCallback(() => {
    setOngoingSolutionData(getInitialSolutionData());
    resetValidation();
  }, [resetValidation]);

  // Helper function to extract non-API dependent data
  const extractImmediateData = useCallback((solutionData) => {
    const industryInfo =
      solutionData?.solution_metadata?.a2a?.industry_info || [];
    const capabilities =
      solutionData?.solution_metadata?.a2a?.capabilities || {};
    const docUrl = solutionData?.solution_metadata?.a2a?.documentationUrl || "";
    const providerVal = solutionData?.solution_metadata?.a2a?.provider || "";

    return {
      solution_name: solutionData?.solution_name || "",
      description: solutionData?.description || "",
      point_of_contact: solutionData?.point_of_contact || "",
      url: "",
      solution_metadata: {},
      approval_status: solutionData?.approval_status || "",
      rejection_reason: solutionData?.rejection_reason || "",
      delete_solution: solutionData?.delete_solution || 0,
      active: solutionData?.active ?? true,
      type: solutionData?.type || "",
      industryInfo,
      capabilities,
      docUrl,
      providerVal,
    };
  }, []);

  // Function to fetch and set filtered agents
  const fetchSetFilteredAgents = useCallback(
    async (solutionData) => {
      const agentsArr =
        solutionData?.solution_metadata?.a2a?.skills?.map(
          (skill) => skill?.id
        ) || [];

      const response = await fetchAgents();
      const agentsFromApi =
        response?.data?.map((agent) => agent?.agent_id) || [];

      const filteredAgents = agentsArr?.filter((currentAgentId) =>
        agentsFromApi?.includes(currentAgentId)
      );

      updateOngoingSolDataL1("agents", filteredAgents);
      validateRequiredField("agents", filteredAgents);
    },
    [fetchAgents, updateOngoingSolDataL1, validateRequiredField]
  );

  // Function to fetch and set filtered business functions
  const fetchSetFilteredBFs = useCallback(
    async (solutionData) => {
      const businessFunctionArr =
        solutionData?.solution_metadata?.a2a?.business_function;

      const response = await fetchSolutionBFs();
      const bFsFromApi = response || [];

      const filteredBFs = businessFunctionArr?.filter((bF) =>
        bFsFromApi?.includes(bF)
      );

      updateOngoingSolDataL3("business_function", filteredBFs);
    },
    [fetchSolutionBFs, updateOngoingSolDataL3]
  );

  const fetchFilteredIndustryInfo = useCallback(
    async (solutionData) => {
      const industryInfoArr =
        solutionData?.solution_metadata?.a2a?.industry_info;

      const industriesFromApi = (await fetchSolutionIndustries()) || [];

      const filteredISs =
        industryInfoArr?.filter((iS) =>
          industriesFromApi?.includes(iS?.industry)
        ) || [];

      let finalISs = [];

      if (filteredISs?.length > 0) {
        try {
          const updatedIndustryInfo = await Promise.allSettled(
            filteredISs?.map(async (item, index) => {
              const sectorValuesFromAPI = await fetchSolutionSectorsSilently(
                item?.industry
              );

              return { ...item, sectorValues: sectorValuesFromAPI || [] };
            })
          );

          finalISs = updatedIndustryInfo?.map((item) => item?.value);
        } catch { }
      }

      const blankObj = { industry: "", sector: [], sectorValues: [] };

      if (finalISs?.length < 3) {
        while (finalISs.length < 3) {
          finalISs.push(blankObj);
        }
      }

      updateOngoingSolDataL3("industry_info", finalISs);

      return finalISs;
    },
    [fetchSolutionIndustries, updateOngoingSolDataL3, fetchSolutionSectors]
  );

  const fetchFilteredProviders = useCallback(
    async (solutionData) => {
      const provider = solutionData?.solution_metadata?.a2a?.provider;

      const response = await fetchSolutionProviders();
      const providersFromApi = response || [];

      const filteredProvider =
        providersFromApi?.find((p) => p === provider) || "";

      updateOngoingSolDataL2("provider", filteredProvider);
    },
    [fetchSolutionProviders, updateOngoingSolDataL2]
  );
  const fetchFilteredStatus = useCallback(
    async (solutionData) => {
      const status = solutionData?.approval_status;

      const response = await fetchSolutionStatus();
      const providersFromApi = response || [];

      const filteredStatus = providersFromApi?.find((p) => p === status) || "";

      updateOngoingSolDataL1("status", filteredStatus);
    },
    [fetchSolutionStatus, updateOngoingSolDataL1]
  );

  const initializeEdit = useCallback(
    async (solutionData) => {
      if (!solutionData) {
        showToastMessage(
          "No solution data provided for edit initialization",
          TOAST_CLASSES?.ERROR
        );
        console.warn("No solution data provided for edit initialization");
        return null;
      }

      try {
        setIsInitializingEdit(true);

        // immediate data
        const immediateData = extractImmediateData(solutionData);

        updateOngoingSolDataL1("solution_name", immediateData.solution_name);
        updateOngoingSolDataL1("description", immediateData.description);
        updateOngoingSolDataL1(
          "point_of_contact",
          immediateData.point_of_contact
        );
        updateOngoingSolDataL1("url", immediateData.url);
        updateOngoingSolDataL1(
          "solution_metadata",
          immediateData.solution_metadata
        );
        updateOngoingSolDataL1(
          "approval_status",
          immediateData.approval_status
        );
        updateOngoingSolDataL1(
          "rejection_reason",
          immediateData.rejection_reason
        );
        updateOngoingSolDataL1(
          "delete_solution",
          immediateData.delete_solution
        );
        updateOngoingSolDataL1("active", immediateData.active);
        updateOngoingSolDataL1("type", immediateData.type);
        updateOngoingSolDataL1("agents", []); // Will be filled later

        updateOngoingSolDataL2("documentation_url", immediateData.docUrl);
        updateOngoingSolDataL2("provider", ""); // Will be filled later

        updateOngoingSolDataL2Capabilities(
          "streaming",
          immediateData.capabilities?.streaming
        );
        updateOngoingSolDataL2Capabilities(
          "push_notifications",
          immediateData.capabilities?.push_notifications
        );
        updateOngoingSolDataL2Capabilities(
          "state_transition_history",
          immediateData.capabilities?.state_transition_history
        );

        updateOngoingSolDataL3("industry_info", []); // Will be filled later
        updateOngoingSolDataL3("business_function", []); // Will be filled later

        // Validate immediate fields
        validateRequiredField("solution_name", immediateData.solution_name);
        validateRequiredField(
          "point_of_contact",
          immediateData.point_of_contact
        );
        validateRequiredField("description", immediateData.description);
        validateRequiredField("documentation_url", immediateData.docUrl);

        // Fetch agents and business functions in parallel
        const loadAsyncData = async () => {
          await Promise.allSettled([
            fetchSetFilteredAgents(solutionData),
            fetchSetFilteredBFs(solutionData),
            fetchFilteredIndustryInfo(solutionData),
            fetchFilteredProviders(solutionData),
            fetchFilteredStatus(solutionData),
          ]);
        };

        await loadAsyncData();
      } catch (err) {
        console.error("Error initializing edit:", err);
      } finally {
        setIsInitializingEdit(false);
      }
    },
    [
      validateRequiredField,
      extractImmediateData,
      updateOngoingSolDataL1,
      updateOngoingSolDataL2,
      updateOngoingSolDataL3,
      updateOngoingSolDataL2Capabilities,
      fetchSetFilteredAgents,
      fetchSetFilteredBFs,
      fetchFilteredIndustryInfo,
      showToastMessage,
      fetchFilteredProviders,
      fetchFilteredStatus,
    ]
  );

  useEffect(() => {
    // console.log("ongoing sol data: ", ongoingSolutionData);
  }, [ongoingSolutionData]);

  // Conditionally fetch agents only on solutions dashboard page (not register/edit)
  useEffect(() => {
    if (
      !location.pathname.includes("register-solution") &&
      !location.pathname.includes("edit-solution")
    ) {
      fetchAgents();
    }
  }, [location.pathname, fetchAgents]);

  const value = {
    solutions,
    selectedSolution,
    ongoingSolutionData,
    isSilentLoading,
    requiredFields,
    solutionCardOpen,
    solutionVersions,
    currentSolutionVersion,
    isLoadingVersions,
    isApprovingRejecting,
    versionsFetchError,
    isLoadingAgents,
    agentsForSol,
    agentsFetchError,
    isUnpublishing,
    solutionDetailsDrawerOpen,
    isLatestVersion,
    isLoadingDropdownFields,
    lookUpValues,
    isFetchingSectorsSilently,
    isInitializingEdit,
    fetchSolutionSectorsSilently,
    setIsLatestVersion,
    changeSolutionDrawerState,
    setIsUnpublishing,
    fetchAgents,
    publishSolution,
    fetchSolutionVersions,
    clearCurrentSolVersion,
    selectSolVersion,
    clearSolVersions,
    setSolutionCardOpen,
    fetchSolutionBFs,
    fetchSolutionProviders,
    fetchSolutionSectors,
    fetchSolutionStatus,
    fetchSolutionIndustries,
    fetchSolutions,
    fetchSolutionsSilently,
    editSolution,
    getSolution,
    deleteSolution,
    setSolutionsData,
    selectSolution,
    clearSelectedSol,
    registerSolution,
    clearOngoingSolutionData,
    initializeEdit,
    updateOngoingSolDataL1,
    updateOngoingSolDataL2,
    updateOngoingSolDataL2Capabilities,
    updateOngoingSolDataL3,
    showSpinner,
    validateRequiredField,
    areAllRequiredFieldsValid,
    resetValidation,
  };

  return (
    <SolutionContext.Provider value={value}>
      {children}

      <Backdrop
        sx={{
          color: "var(--db-primary-white)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: "134vh !important",
        }}
        open={
          showLoader ||
          isFetchingSolutions ||
          isDeleting ||
          isRegisteringOrEditingSol ||
          isApprovingRejecting
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </SolutionContext.Provider>
  );
};

// Custom hook for using the context
export const useSolution = () => {
  const context = useContext(SolutionContext);

  if (!context) {
    throw new Error("useSolution must be used within a SolutionsProvider");
  }

  return context;
};

export default SolutionContext;
